import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { CodeModel } from 'src/app/models/code.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-codes-view',
  templateUrl: './sites-codes-view.component.html',
  styleUrls: ['./sites-codes-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesCodesViewComponent implements OnInit {
  parent_id: number;
  child_id: number;
  site_id: number;

  code: CodeModel = new CodeModel();

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission checks on every change detection cycle.
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    public utils: UtilsService,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve the RBAC permission flags used by the template before any early return.
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Get the site id from the route params first (needed for redirect)
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);
    this.site_id = this.child_id ? this.child_id : this.parent_id;

    // Check permissions - user must have sites:view AND sites-qr-codes:view
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_QR_CODES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view QR codes.'
      );
      this.router.navigate([
        this.utils.prepareSiteRelatedLink(
          this.site_id,
          this.child_id ? this.parent_id : null,
          'edit'
        )
      ]);
      return;
    }

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      this.code.id = Number(this.route.snapshot.params['code_id']);
      if (!this.code.id) {
        this.router.navigate([
          this.utils.prepareSiteRelatedLink(
            this.site_id,
            this.child_id ? this.parent_id : null,
            'edit/codes'
          )
        ]);
        return;
      }

      this.api
        .makeRequest(
          'get',
          'v1/sites/' + this.site_id + '/qrcodes/' + this.code.id
        )
        .then((response: any) => {
          this.code.apply(response.data);
        });
    }
  }

  onRemove() {
    // Check permissions - user must have sites:view AND sites-qr-codes:view AND sites-qr-codes:delete
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_QR_CODES,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.utils.showToast('You do not have permission to delete QR codes.');
      return;
    }

    this.utils.showModal(
      'Remove QR Code',
      'Are you sure you want to remove this QR code?',
      () => {
        this.api
          .makeRequest(
            'delete',
            'v1/sites/' + this.site_id + '/qrcodes/' + this.code.id
          )
          .then((response: any) => {
            this.utils.showToast('The QR code was removed.');
            this.router.navigate([
              this.utils.prepareSiteRelatedLink(
                this.site_id,
                this.child_id ? this.parent_id : null,
                'edit/codes'
              )
            ]);
          });
      }
    );
  }

  protected readonly RBACService = RBACService;
}
